import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';

// Categories an admin can switch absent emails off for. Keys match
// server/helpers/absentEmailRule.js.
const ABSENT_EMAIL_CATEGORIES = [
  { key: 'training',          label: 'Training',                 hint: 'Regular classes' },
  { key: 'assessment',        label: 'Assessment: per duration', hint: 'No assessment basis set' },
  { key: 'assessment:fa_1_2', label: 'Assessment F.A. 1.2.',     hint: 'Per pax' },
  { key: 'assessment:fa_2_2', label: 'Assessment F.A. 2.2.',     hint: 'Final' },
];

export default function SettingsPage() {
  const [form, setForm] = useState({
    cookie_expiry_days: '365',
    allow_phone_change: 'false',
    present_minutes:    '30',
    late_minutes:       '60',
    qr_window_minutes:  '30',
    auto_end_class:      'false',
    auto_end_class_since: '',
    absent_email_off:    '',
  });
  const [loading, setSaving]  = useState(false);
  const [fetching, setFetching] = useState(true);
  const [checkingAutoEnd, setCheckingAutoEnd] = useState(false);

  useEffect(() => {
    api.get('/api/settings')
      .then(res => {
        const d = res.data.data || {};
        setForm(prev => ({ ...prev, ...d }));
      })
      .catch(() => toast.error('Failed to load settings.'))
      .finally(() => setFetching(false));
  }, []);

  function set(key, value) { setForm(p => ({ ...p, [key]: value })); }

  const absentEmailOff = String(form.absent_email_off || '').split(',').map(s => s.trim()).filter(Boolean);
  function toggleAbsentEmail(category, sendEmail) {
    const next = sendEmail
      ? absentEmailOff.filter(c => c !== category)
      : [...absentEmailOff.filter(c => c !== category), category];
    set('absent_email_off', next.join(','));
  }

  // Turning Auto End Class on can immediately catch up a backlog of overdue sessions
  // once saved (each triggers absent-marking + notifications + a report email), so ask
  // the admin what they want before flipping the switch.
  async function handleAutoEndToggle(checked) {
    if (!checked) {
      setForm(p => ({ ...p, auto_end_class: 'false' }));
      return;
    }
    setCheckingAutoEnd(true);
    try {
      const res = await api.get('/api/sessions/overdue-count');
      const count = res.data.count || 0;
      if (count > 0) {
        const endNow = window.confirm(
          `${count} session${count === 1 ? ' is' : 's are'} already past its end time and hasn't been ended.\n\n` +
          `End ${count === 1 ? 'it' : 'them'} now as well? Each will mark no-shows absent and email the attendance report.\n\n` +
          `Click Cancel to leave ${count === 1 ? 'it' : 'them'} for manual handling — Auto End Class will only apply to sessions ending from now on.`
        );
        if (endNow) {
          await api.post('/api/sessions/end-overdue');
          toast.success(`Ended ${count} overdue session${count === 1 ? '' : 's'}.`);
          setForm(p => ({ ...p, auto_end_class: 'true', auto_end_class_since: '' }));
        } else {
          const timeRes = await api.get('/api/server-time');
          setForm(p => ({ ...p, auto_end_class: 'true', auto_end_class_since: timeRes.data.sgt_display }));
        }
      } else {
        setForm(p => ({ ...p, auto_end_class: 'true', auto_end_class_since: '' }));
      }
    } catch (err) {
      toast.error('Failed to check overdue sessions — please try again.');
    } finally {
      setCheckingAutoEnd(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();

    // Validate
    const presentMins  = parseInt(form.present_minutes);
    const lateMins     = parseInt(form.late_minutes);
    const cookieDays   = parseInt(form.cookie_expiry_days);
    const qrWindowMins = parseInt(form.qr_window_minutes);

    if (isNaN(presentMins) || presentMins < 1) {
      toast.error('Present threshold must be at least 1 minute.'); return;
    }
    if (isNaN(lateMins) || lateMins <= presentMins) {
      toast.error('Late threshold must be greater than Present threshold.'); return;
    }
    if (isNaN(cookieDays) || cookieDays < 0) {
      toast.error('Cookie expiry must be 0 or more days.'); return;
    }
    if (isNaN(qrWindowMins) || qrWindowMins < 1) {
      toast.error('QR window must be at least 1 minute.'); return;
    }

    setSaving(true);
    try {
      await api.put('/api/settings', form);
      toast.success('Settings saved successfully.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  if (fetching) return (
    <div className="text-center py-5">
      <div className="spinner-border" style={{ color: '#17a2b8' }} />
    </div>
  );

  const presentMins = parseInt(form.present_minutes) || 30;
  const lateMins    = parseInt(form.late_minutes)    || 60;

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p>System-wide configuration for the QR Attendance System</p>
      </div>

      <form onSubmit={handleSave}>
        <div className="d-flex flex-column gap-4">

          {/* ── Cookie Settings ──────────────────────────────────── */}
          <div className="tmis-card" style={{padding: '25px'}}>
            <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <i className="bi bi-phone text-primary" />
              Remember My Phone Number
            </h6>
            <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
              When a student scans the QR code and enters their phone number, the system can save it so they don't need to type it again next time.
            </p>

            <div className="row g-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Remember for <span className="text-muted fw-normal">(days)</span>
                </label>
                <div className="input-group" style={{ maxWidth: 220 }}>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="3650"
                    value={form.cookie_expiry_days}
                    onChange={e => set('cookie_expiry_days', e.target.value)}
                  />
                  <span className="input-group-text">days</span>
                </div>
                <div className="form-text">
                  {form.cookie_expiry_days === '0'
                    ? '⚠ Disabled — students must type their phone number every time they scan.'
                    : `Students won't need to type their phone number again for ${form.cookie_expiry_days} days.`}
                  <br />
                  <strong>Note:</strong> Changing this only applies to new scans. Students who have already scanned will keep their current saved number until it naturally expires.
                </div>
              </div>

              <div className="col-md-6 tmis-card-col-divider">
                <label className="form-label fw-semibold">Allow Students to Update Their Saved Number</label>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allow_phone_change"
                    style={{ width: 40, height: 22 }}
                    checked={form.allow_phone_change === 'true'}
                    onChange={e => set('allow_phone_change', e.target.checked ? 'true' : 'false')}
                  />
                  <label className="form-check-label ms-2" htmlFor="allow_phone_change">
                    {form.allow_phone_change === 'true'
                      ? 'Turned on — students can tap "Not you?" to enter a different number'
                      : 'Turned off — students cannot change the number saved on their device'}
                  </label>
                </div>
                <div className="form-text">
                  When turned off, students are locked to the phone number saved on their device. Turn this on temporarily if a student has changed their SIM card or phone number, then turn it off again once done.
                </div>
              </div>
            </div>
          </div>

          {/* ── Attendance Status Rules ──────────────────────────── */}
          <div className="tmis-card" style={{padding: '25px'}}>
            <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <i className="bi bi-clock-history text-warning" />
              Attendance Status Rules
            </h6>
            <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
              Controls when a student is marked Present, Late, or Absent based on when they scan the QR code. All times are counted in minutes <strong>after</strong> the class start time.
            </p>

            <div className="row g-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  <span className="badge bg-success me-2">Present</span>
                  Up to (minutes after start)
                </label>
                <div className="input-group" style={{ maxWidth: 180 }}>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="120"
                    value={form.present_minutes}
                    onChange={e => set('present_minutes', e.target.value)}
                  />
                  <span className="input-group-text">min</span>
                </div>
                <div className="form-text">Scanned within {form.present_minutes} min of the class start time → marked <strong>Present</strong></div>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  <span className="badge text-dark me-2" style={{ backgroundColor:'#ffd24b' }}>Late</span>
                  Up to (minutes after start)
                </label>
                <div className="input-group" style={{ maxWidth: 180 }}>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="240"
                    value={form.late_minutes}
                    onChange={e => set('late_minutes', e.target.value)}
                    className={`form-control ${parseInt(form.late_minutes) <= parseInt(form.present_minutes) ? 'is-invalid' : ''}`}
                  />
                  <span className="input-group-text">min</span>
                </div>
                {parseInt(form.late_minutes) <= parseInt(form.present_minutes) && (
                  <div className="invalid-feedback d-block">Must be greater than Present threshold.</div>
                )}
                <div className="form-text">Scanned between {presentMins}–{form.late_minutes} min after start → marked <strong>Late</strong></div>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  <span className="badge bg-danger me-2">Absent</span>
                  After the Late window
                </label>
                <div className="form-control-plaintext fw-bold text-danger">
                  &gt; {form.late_minutes} min after start
                </div>
                <div className="form-text">Did not scan at all, or scanned more than {form.late_minutes} min after start</div>
              </div>
            </div>

            {/* Visual timeline */}
            <div className="mt-4 p-3 rounded-3" style={{ background: 'var(--tmis-table-row-hover)' }}>
              <div className="d-flex align-items-center gap-0 mb-1" style={{ fontSize: '0.75rem', color: 'var(--tmis-muted)' }}>
                <span style={{ minWidth: 60 }}>Start</span>
                <div className="d-flex gap-2">
                  <span style={{ minWidth: Math.max(60, presentMins * 2) + 'px', background: 'var(--tmis-pill-present-bg)', border: '1px solid var(--tmis-border)', borderRadius: 4, padding: '2px 8px', textAlign: 'center', color: 'var(--tmis-pill-present-text)' }}>
                     Present (0–{presentMins}m)
                  </span>
                  <span style={{ minWidth: Math.max(40, (lateMins - presentMins) * 2) + 'px', background: 'var(--tmis-pill-late-bg)', border: '1px solid var(--tmis-border)', borderRadius: 4, padding: '2px 8px', textAlign: 'center', color: 'var(--tmis-pill-late-text)' }}>
                     Late ({presentMins}–{lateMins}m)
                  </span>
                  <span style={{ minWidth: 80, background: 'var(--tmis-pill-absent-bg)', border: '1px solid var(--tmis-border)', borderRadius: 4, padding: '2px 8px', textAlign: 'center', color: 'var(--tmis-pill-absent-text)' }}>
                     Absent (&gt;{lateMins}m)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── QR Visibility Window + Auto End Class ─────────────── */}
          <div className="tmis-card" style={{padding: '25px'}}>
            <div className="row g-4">
              <div className="col-12 col-xl-4">
                <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                  <i className="bi bi-qr-code text-primary" />
                  QR Visibility Window
                </h6>
                <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                  How early the Clock-In QR code becomes available on the Home page, before a session's scheduled start time.
                </p>

                <label className="form-label fw-semibold">Opens (minutes before start)</label>
                <div className="input-group" style={{ maxWidth: 180 }}>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="180"
                    value={form.qr_window_minutes}
                    onChange={e => set('qr_window_minutes', e.target.value)}
                  />
                  <span className="input-group-text">min</span>
                </div>
                <div className="form-text">QR codes will appear {form.qr_window_minutes} minutes before class starts.</div>
              </div>

              <div className="col-12 col-xl-4 tmis-card-col-divider-xl">
                <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                  <i className="bi bi-stop-circle text-danger" />
                  Auto End Class
                </h6>
                <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                  Controls whether a session ends itself once its scheduled end time passes, or whether the trainer must tap "End" manually.
                </p>

                <label className="form-label fw-semibold">Automatically End Class</label>
                <div className="form-check form-switch mt-2 d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="auto_end_class"
                    style={{ width: 40, height: 22 }}
                    checked={form.auto_end_class === 'true'}
                    disabled={checkingAutoEnd}
                    onChange={e => handleAutoEndToggle(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="auto_end_class">
                    {checkingAutoEnd
                      ? 'Checking for overdue sessions…'
                      : form.auto_end_class === 'true'
                        ? 'Turned on — sessions end automatically once their scheduled end time passes'
                        : 'Turned off — trainers must tap "End" to finalize attendance'}
                  </label>
                  {checkingAutoEnd && <span className="spinner-border spinner-border-sm" />}
                </div>
                <div className="form-text">
                  Only applies to sessions with a scheduled end time set. Ending a class — manually or automatically — marks no-shows absent, syncs medical leave, and emails the attendance report.
                  {form.auto_end_class === 'true' && form.auto_end_class_since && (
                    <><br /><strong>Note:</strong> only sessions ending on or after {form.auto_end_class_since} SGT are auto-ended — older overdue sessions were left as-is and need to be ended manually.</>
                  )}
                </div>
              </div>

              <div className="col-12 col-xl-4 tmis-card-col-divider-xl">
                <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                  <i className="bi bi-envelope-slash text-danger" />
                  Absent Email
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Choose which sessions email students when they're marked Absent — for both late scans and no-shows. Turn a switch off to stop the email.
                </p>

                <div className="absent-email-list">
                  {ABSENT_EMAIL_CATEGORIES.map(cat => {
                    const sends = !absentEmailOff.includes(cat.key);
                    const id = `absent_email_${cat.key.replace(/\W/g, '_')}`;
                    return (
                      <div className="absent-email-row" key={cat.key}>
                        <label htmlFor={id} className="absent-email-copy">
                          <span className="fw-semibold">{cat.label}</span>
                          <span className="text-muted d-block" style={{ fontSize: '0.8rem' }}>{cat.hint}</span>
                        </label>
                        <div className="d-flex align-items-center gap-3">
                          <span className={`stat-pill ${sends ? 'present' : 'absent'}`}>{sends ? 'Email sent' : 'No email'}</span>
                          <label className="session-toggle" htmlFor={id}>
                            <input
                              className="session-toggle-input"
                              type="checkbox"
                              role="switch"
                              id={id}
                              checked={sends}
                              onChange={e => toggleAbsentEmail(cat.key, e.target.checked)}
                            />
                            <span className="session-toggle-track" aria-hidden="true"><span /></span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="form-text">
                  Students are still marked Absent in attendance and reports. The student's own email preference still applies.
                </div>
              </div>
            </div>
          </div>

          {/* ── Save ─────────────────────────────────────────────── */}
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-tmis-primary d-flex align-items-center gap-2"
              disabled={loading}
            >
              {loading && <span className="spinner-border spinner-border-sm" />}
              <i className="bi bi-save" />
              Save Settings
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}